import React, { useState } from 'react';

const FONT = '"Plus Jakarta Sans", system-ui, sans-serif';

interface ShareButtonsProps {
  url: string;
  title: string;
  theme?: 'light' | 'dark';
}

interface Platform {
  name: string;
  color: string;
  href: (url: string, title: string) => string;
  Icon: () => React.ReactElement;
}

const platforms: Platform[] = [
  {
    name: 'Reddit',
    color: '#FF4500',
    href: (url, title) => `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`,
    Icon: () => (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
      </svg>
    ),
  },
  {
    name: 'LinkedIn',
    color: '#0A66C2',
    href: (url) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    Icon: () => (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    ),
  },
  {
    name: 'Hacker News',
    color: '#FF6600',
    href: (url, title) => `https://news.ycombinator.com/submitlink?u=${encodeURIComponent(url)}&t=${encodeURIComponent(title)}`,
    Icon: () => (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M0 24V0h24v24H0zM6.951 5.896l4.112 7.708v5.064h1.583v-4.972l4.148-7.799h-1.749l-2.457 4.875c-.372.745-.688 1.434-.688 1.434s-.297-.708-.651-1.434L8.831 5.896H6.95z" />
      </svg>
    ),
  },
  {
    name: 'Facebook',
    color: '#1877F2',
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    Icon: () => (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
  },
  {
    name: 'Dev.to',
    color: '#3b49df',
    href: (url, title) => `https://dev.to/new?prefill=${encodeURIComponent(`---\ntitle: ${title}\ncanonical_url: ${url}\ntags: aws, finops, cloud\n---\n\n`)}`,
    Icon: () => (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7.42 10.05c-.18-.16-.46-.23-.84-.23H6l.02 2.44.04 2.45.56-.02c.41 0 .63-.07.83-.26.24-.24.26-.36.26-2.2 0-1.91-.02-1.96-.29-2.18zM0 4.94v14.12h24V4.94H0zM8.56 15.3c-.44.58-1.06.77-2.53.77H4.71V8.53h1.4c1.67 0 2.16.18 2.6.9.27.43.29.6.32 2.57.05 2.23-.02 2.73-.47 3.3zm5.09-5.47h-2.47v1.77h1.52v1.28l-.72.04-.75.03v1.77l1.22.03 1.2.04v1.28h-1.6c-1.53 0-1.6-.01-1.87-.3l-.3-.28v-3.16c0-3.02.01-3.18.25-3.48.23-.31.25-.31 1.88-.31h1.64v1.29zm4.68 5.45c-.17.43-.64.79-1 .79-.18 0-.45-.15-.67-.39-.32-.32-.45-.63-.82-2.08l-.9-3.39-.45-1.67h.76c.4 0 .75.02.75.05 0 .06 1.16 4.54 1.26 4.83.04.15.32-.7.73-2.3l.66-2.52.74-.04c.4-.02.73 0 .73.04 0 .14-1.67 6.38-1.8 6.68z" />
      </svg>
    ),
  },
];

export default function ShareButtons({ url, title, theme = 'light' }: ShareButtonsProps) {
  const [open, setOpen] = useState(false);

  const triggerBg = theme === 'light' ? 'rgba(255,255,255,0.1)' : 'rgba(15,61,46,0.08)';
  const triggerColor = theme === 'light' ? 'rgba(255,255,255,0.6)' : 'rgba(15,22,32,0.5)';
  const triggerBorder = theme === 'light' ? 'rgba(255,255,255,0.15)' : 'rgba(15,61,46,0.15)';
  const iconBg = theme === 'light' ? 'rgba(255,255,255,0.1)' : 'rgba(15,61,46,0.07)';
  const iconColor = theme === 'light' ? 'rgba(255,255,255,0.65)' : 'rgba(15,22,32,0.5)';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontFamily: FONT }}>
      {/* Trigger pill */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          padding: '5px 12px', borderRadius: '99px',
          background: open ? 'rgba(232,184,76,0.12)' : triggerBg,
          border: `1px solid ${open ? 'rgba(232,184,76,0.3)' : triggerBorder}`,
          color: open ? '#E8B84C' : triggerColor,
          fontSize: '12px', fontWeight: 600, fontFamily: FONT,
          cursor: 'pointer', transition: 'all 0.2s',
        }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
        </svg>
        Share
      </button>

      {/* Animated icon strip */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden', maxWidth: open ? '200px' : '0px', opacity: open ? 1 : 0, transition: 'max-width 0.3s cubic-bezier(0.4,0,0.2,1), opacity 0.2s ease' }}>
        {platforms.map((p, i) => (
          <a
            key={p.name}
            href={p.href(url, title)}
            target="_blank"
            rel="noopener noreferrer"
            title={p.name}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: '28px', height: '28px', borderRadius: '8px',
              background: iconBg, color: iconColor,
              textDecoration: 'none', flexShrink: 0,
              transition: `background 0.15s, color 0.15s, transform 0.2s`,
              transitionDelay: `${i * 30}ms`,
              transform: open ? 'scale(1)' : 'scale(0.7)',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = p.color + '25';
              (e.currentTarget as HTMLAnchorElement).style.color = p.color;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = iconBg;
              (e.currentTarget as HTMLAnchorElement).style.color = iconColor;
            }}
          >
            <p.Icon />
          </a>
        ))}
      </div>
    </div>
  );
}
